import UpNext from "../types/UpNext";
import Work, { WorkGrouping } from "../types/Work";

// What a work is grouped in, and its place there. Components read a work's
// series, series id and number through these, so ADR 0024's groupings
// change this module rather than every reader. A TV show is one work, its
// seasons parts of it (ADR 0030).

// The series a work is in: its id, and its title when the work carries its
// series, as the API sends it (a person's credits among them); null when a
// work carries only its series id.
export type Grouping = { id: number; title: string | null };

export const groupingOf = (work: Work): Grouping | null => {
  const id = work.series?.id ?? work.seriesId;
  return id != null ? { id, title: work.series?.title ?? null } : null;
};

// Every grouping a work's page names under its title, the main series
// first (plan 020's Phase 4): none where they'd say no more than the
// breadcrumb, its one series.
export const groupingsOf = (work: Work): WorkGrouping[] => {
  const all = work.groupings ?? [];
  const shown = groupingOf(work)?.id;
  return all.some((grouping) => grouping.id !== shown) ? all : [];
};

// A work's number in its series ("book 2"); null when it has none. A
// Continue card's volumes have one too.
export const placeOf = (work: {
  sequenceNumber: number | null;
}): number | null => work.sequenceNumber;

// A season as a label names it ("Season 2", beside Continue's kicker and
// over an episode's title): the season an episode of a show carries (ADR
// 0030); null without one.
export const seasonLabel = (season?: number | null): string | null =>
  season != null ? `Season ${season}` : null;

// Where a show's season is listed: its page at that season (`?season=K`),
// else its page.
export const seasonPath = (
  work: Work,
  episodeSeason?: number | null,
): string =>
  episodeSeason != null
    ? `/works/${work.id}?season=${episodeSeason}`
    : `/works/${work.id}`;

// A Continue card's place on the shelf, as the API's `slot_of` puts it: the
// series of a work in one, which the shelf holds one item for, so ticking an
// item off turns its card into the next one in place; else the work. A show
// keeps its own, as the API's does, so a grouping a show is in (ADR 0024)
// never merges two into one card.
export const slotKey = ({ work }: UpNext): string => {
  const grouping = work.workType === "tv" ? null : groupingOf(work);
  return grouping ? `series:${grouping.id}` : `work:${work.id}`;
};
